import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PaletteIcon from "@mui/icons-material/Palette";
import PaymentIcon from "@mui/icons-material/Payment";
import ReplyIcon from "@mui/icons-material/Reply";
import ShieldIcon from "@mui/icons-material/Shield";
import StraightenIcon from "@mui/icons-material/Straighten";
import TimerIcon from "@mui/icons-material/Timer";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const RAW_API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const TURQUOISE = "#00B4A6";
const TURQUOISE_DARK = "#00796B";
const TURQUOISE_MIST = "#DDFBF8";

const GOLD = "#D4AF37";
const GOLD_DARK = "#8A6500";
const GOLD_MIST = "#FFF4CF";

const BURGUNDY = "#7A1232";
const BURGUNDY_DARK = "#4A071D";
const BURGUNDY_MIST = "#FBEAF0";

const INK = "#071313";
const MUTED = "#60706D";
const WHITE = "#ffffff";
const LINE = "rgba(7, 19, 19, 0.10)";

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";

function buildApiUrl(path) {
  const base = RAW_API_BASE_URL.replace(/\/+$/, "");
  const cleanPath = path.replace(/^\/+/, "");

  if (!base) {
    return `/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
  }

  if (base.endsWith("/api")) {
    return `${base}/${cleanPath.replace(/^api\//, "")}`;
  }

  return `${base}/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
}

function forceHttpsUrl(url) {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (cleanUrl.startsWith("http://")) {
    return `https://${cleanUrl.slice("http://".length)}`;
  }

  return cleanUrl;
}

function moneyNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === "") return fallback;

  const clean = String(value).replace("£", "").replace(",", "").trim();
  const number = Number(clean);

  if (!Number.isFinite(number)) return fallback;

  return number;
}

function moneyLabel(value, fallback = "TBC") {
  if (value === null || value === undefined || value === "") return fallback;

  const number = moneyNumber(value, null);

  if (number === null) return String(value);

  return `£${number.toFixed(2)}`;
}

function getCheckoutUrl(data) {
  return (
    data?.checkout_url ||
    data?.stripe_checkout_url ||
    data?.payment_url ||
    data?.url ||
    ""
  );
}

function getArtworkTitle(data) {
  return (
    data?.painting_title ||
    data?.artwork_title ||
    data?.painting?.title ||
    "Untitled artwork"
  );
}

function getArtworkImageUrl(data) {
  return forceHttpsUrl(
    data?.painting_image_url ||
      data?.artwork_image_url ||
      data?.painting?.image_url ||
      data?.painting?.image ||
      ""
  );
}

function getArtworkArtist(data) {
  return (
    data?.painting_artist_name ||
    data?.artist_name ||
    data?.painting_buyer_facing_artist_name ||
    data?.buyer_facing_artist_name ||
    data?.painting?.buyer_facing_artist_name ||
    data?.painting?.artist_name ||
    "Artist"
  );
}

function getArtworkMedium(data) {
  return (
    data?.painting_medium ||
    data?.artwork_medium ||
    data?.painting?.medium ||
    "Artwork"
  );
}

function getArtworkSize(data) {
  return (
    data?.painting_size_label ||
    data?.artwork_size_label ||
    data?.painting?.size_label ||
    data?.painting?.size ||
    "Size TBC"
  );
}

function getArtworkCategory(data) {
  const raw =
    data?.painting_artwork_category ||
    data?.artwork_category ||
    data?.painting?.artwork_category ||
    "artwork";

  const labels = {
    painting: "Painting",
    print: "Print",
    sculpture: "Sculpture",
    decorative_object: "Decorative Object",
    artwork: "Artwork",
  };

  return labels[raw] || raw;
}

function getOfferFloor(data) {
  return (
    data?.painting_minimum_offer_label ||
    data?.painting_current_offer_floor ||
    data?.painting?.minimum_offer_label ||
    moneyLabel(data?.painting?.current_offer_minimum_gbp, "Offer floor TBC")
  );
}

function getStatusLabel(status) {
  const labels = {
    pending: "Waiting for artist",
    countered: "Artist countered",
    buyer_countered: "Buyer countered",
    accepted: "Accepted",
    payment_pending: "Payment pending",
    declined: "Declined",
    expired: "Expired",
    withdrawn: "Withdrawn",
    paid: "Paid",
    cancelled: "Cancelled",
  };

  return labels[status] || status || "Pending";
}

function isFinalStatus(status) {
  return ["declined", "expired", "withdrawn", "paid", "cancelled"].includes(status);
}

function canPayStatus(status) {
  return ["accepted", "payment_pending"].includes(status);
}

function formatDateTime(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getTimeLeft(value, now = Date.now()) {
  if (!value) return "";

  const deadline = new Date(value).getTime();

  if (Number.isNaN(deadline)) return "";

  const diff = deadline - now;

  if (diff <= 0) return "Expired";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff / (1000 * 60)) % 60);

  if (hours <= 0) return `${minutes}m left`;

  return `${hours}h ${minutes}m left`;
}

const PageShell = styled(Box)({
  minHeight: "100vh",
  background:
    `radial-gradient(circle at 0% 0%, ${alpha(TURQUOISE, 0.16)}, transparent 30%),
     radial-gradient(circle at 100% 0%, ${alpha(GOLD, 0.15)}, transparent 28%),
     radial-gradient(circle at 100% 78%, ${alpha(BURGUNDY, 0.12)}, transparent 26%),
     linear-gradient(180deg, #fbfffe 0%, #f7fffd 48%, #fffaf0 100%)`,
  color: INK,
  fontFamily: BODY_FONT,
});

const Panel = styled(Paper)({
  borderRadius: 28,
  border: `1px solid ${LINE}`,
  background: alpha(WHITE, 0.94),
  boxShadow: "0 18px 60px rgba(7, 19, 19, 0.07)",
  overflow: "hidden",
});

const DarkPanel = styled(Paper)({
  borderRadius: 30,
  background:
    `linear-gradient(135deg, ${BURGUNDY_DARK} 0%, #092b28 56%, #171105 100%)`,
  color: WHITE,
  border: `1px solid ${alpha(WHITE, 0.12)}`,
  boxShadow: "0 24px 70px rgba(7, 19, 19, 0.22)",
  overflow: "hidden",
});

const PrimaryButton = styled(Button)({
  textTransform: "none",
  fontWeight: 900,
  borderRadius: 999,
  padding: "12px 20px",
  background: `linear-gradient(135deg, ${TURQUOISE} 0%, ${GOLD} 100%)`,
  color: WHITE,
  boxShadow: `0 14px 30px ${alpha(TURQUOISE, 0.22)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${TURQUOISE_DARK} 0%, ${GOLD_DARK} 100%)`,
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: alpha(TURQUOISE, 0.35),
    color: alpha(WHITE, 0.82),
  },
});

const BurgundyButton = styled(Button)({
  textTransform: "none",
  fontWeight: 900,
  borderRadius: 999,
  padding: "12px 20px",
  background: `linear-gradient(135deg, ${BURGUNDY} 0%, ${BURGUNDY_DARK} 100%)`,
  color: WHITE,
  boxShadow: `0 14px 30px ${alpha(BURGUNDY, 0.18)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${BURGUNDY_DARK} 0%, ${BURGUNDY} 100%)`,
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: alpha(BURGUNDY, 0.32),
    color: alpha(WHITE, 0.82),
  },
});

const SoftButton = styled(Button)({
  textTransform: "none",
  fontWeight: 850,
  borderRadius: 999,
  padding: "10px 16px",
  color: TURQUOISE_DARK,
  border: `1px solid ${alpha(TURQUOISE, 0.32)}`,
  background: WHITE,
  "&:hover": {
    background: TURQUOISE_MIST,
    borderColor: TURQUOISE,
  },
});

const MinimalInput = styled(TextField)({
  "& label": {
    color: alpha(INK, 0.58),
  },
  "& label.Mui-focused": {
    color: TURQUOISE_DARK,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 18,
    background: WHITE,
    "& fieldset": {
      borderColor: LINE,
    },
    "&:hover fieldset": {
      borderColor: alpha(TURQUOISE, 0.45),
    },
    "&.Mui-focused fieldset": {
      borderColor: TURQUOISE,
    },
  },
});

const MetaBox = ({ icon, label, value, tone = "turquoise" }) => {
  const toneStyles = {
    turquoise: {
      background: TURQUOISE_MIST,
      color: TURQUOISE_DARK,
    },
    gold: {
      background: GOLD_MIST,
      color: GOLD_DARK,
    },
    burgundy: {
      background: BURGUNDY_MIST,
      color: BURGUNDY,
    },
  };

  const selectedTone = toneStyles[tone] || toneStyles.turquoise;

  return (
    <Box
      sx={{
        p: 1.6,
        borderRadius: 4,
        bgcolor: selectedTone.background,
        border: `1px solid ${alpha(selectedTone.color, 0.12)}`,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            bgcolor: WHITE,
            color: selectedTone.color,
            flex: "0 0 auto",
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: alpha(INK, 0.56),
              fontSize: 12,
              fontWeight: 850,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              color: INK,
              fontWeight: 950,
              overflowWrap: "anywhere",
            }}
          >
            {value || "TBC"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default function BuyerOfferResponsePage() {
  const { offerId, token } = useParams();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());

  const [buyerMessage, setBuyerMessage] = useState("");
  const [buyerCounterAmount, setBuyerCounterAmount] = useState("");

  const detailUrl = useMemo(
    () => buildApiUrl(`room-offers/${offerId}/buyer-response/${token}/`),
    [offerId, token]
  );

  const paymentUrl = useMemo(
    () => buildApiUrl("room-offers/payment/create/"),
    []
  );

  const actionUrl = (action) =>
    buildApiUrl(`room-offers/${offerId}/buyer-response/${token}/${action}/`);

  const artworkTitle = getArtworkTitle(offer);
  const artworkImageUrl = getArtworkImageUrl(offer);
  const artworkArtist = getArtworkArtist(offer);
  const artworkMedium = getArtworkMedium(offer);
  const artworkSize = getArtworkSize(offer);
  const artworkCategory = getArtworkCategory(offer);

  const buyerOfferLabel = moneyLabel(offer?.amount_gbp, "Offer TBC");
  const artistCounterLabel = moneyLabel(offer?.counter_amount_gbp, "Counter TBC");
  const acceptedAmountLabel = moneyLabel(offer?.accepted_amount_gbp, "");
  const postageLabel =
    offer?.postage_fee_label ||
    moneyLabel(offer?.postage_fee_gbp, "Postage TBC");
  const totalLabel =
    offer?.total_due_label ||
    moneyLabel(offer?.total_due_gbp, "Total TBC");

  const offerFloor = getOfferFloor(offer);
  const statusLabel = getStatusLabel(offer?.status);
  const expiresAtLabel = formatDateTime(offer?.expires_at || offer?.payment_due_at);
  const timeLeft = getTimeLeft(offer?.expires_at || offer?.payment_due_at, nowTick);

  const buyerOffersUsed = offer?.buyer_offer_count ?? 1;
  const artistCountersUsed = offer?.artist_counter_count ?? 0;

  const buyerOffersLeft =
    offer?.remaining_buyer_offer_chances ??
    Math.max(
      0,
      Number(offer?.max_buyer_offer_chances || 3) -
        Number(offer?.buyer_offer_count || 1)
    );

  const finalStatus = isFinalStatus(offer?.status);
  const payableStatus = canPayStatus(offer?.status);
  const expiredByClock =
    (offer?.expires_at || offer?.payment_due_at) &&
    getTimeLeft(offer?.expires_at || offer?.payment_due_at, nowTick) === "Expired";

  const responseActionsDisabled =
    Boolean(actionLoading) ||
    finalStatus ||
    payableStatus ||
    expiredByClock;

  const payActionsDisabled =
    Boolean(actionLoading) ||
    finalStatus ||
    expiredByClock ||
    !payableStatus;

  useEffect(() => {
    document.title = "Respond to Counter Offer | Art For My Room";

    const description =
      "Secure buyer counter-offer response page for Art For My Room.";

    let meta = document.querySelector('meta[name="description"]');

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", description);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowTick(Date.now());
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchOffer = async () => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        const response = await fetch(detailUrl, {
          headers: {
            Accept: "application/json",
          },
        });

        let data = {};

        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (!response.ok) {
          throw new Error(
            data.error ||
              data.message ||
              "This secure counter-offer link could not be opened."
          );
        }

        setOffer(data);

        const currentBuyerAmount = moneyNumber(data.amount_gbp, 0);
        const artistCounterAmount = moneyNumber(data.counter_amount_gbp, 0);

        if (currentBuyerAmount > 0) {
          setBuyerCounterAmount(currentBuyerAmount.toFixed(2));
        } else if (artistCounterAmount > 0) {
          setBuyerCounterAmount(artistCounterAmount.toFixed(2));
        }

        setBuyerMessage("");
      } catch (err) {
        setError(err.message || "This offer could not be loaded.");
      } finally {
        setLoading(false);
      }
    };

    if (offerId && token) {
      fetchOffer();
    } else {
      setLoading(false);
      setError("The offer link is missing information.");
    }
  }, [detailUrl, offerId, token]);

  const refreshOffer = async () => {
    try {
      const response = await fetch(detailUrl, {
        headers: {
          Accept: "application/json",
        },
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok) {
        setOffer(data);
      }
    } catch {
      // Keep the user-visible action result.
    }
  };

  const redirectToPayment = async (dataFromAccept = {}) => {
    const directCheckoutUrl = getCheckoutUrl(dataFromAccept);

    if (directCheckoutUrl) {
      window.location.href = directCheckoutUrl;
      return;
    }

    const response = await fetch(paymentUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        offer_id: offerId,
      }),
    });

    let data = {};

    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      throw new Error(
        data.error ||
          data.message ||
          data.detail ||
          "Payment link could not be created."
      );
    }

    const checkoutUrl = getCheckoutUrl(data);

    if (!checkoutUrl) {
      throw new Error("Payment link was created, but no Stripe checkout URL was returned.");
    }

    window.location.href = checkoutUrl;
  };

  const submitBuyerAction = async (action) => {
    if (!offerId || !token) {
      setError("The offer link is missing information.");
      return;
    }

    if (action !== "pay" && responseActionsDisabled) {
      setError("This counter-offer is no longer open for response.");
      return;
    }

    if (action === "pay" && payActionsDisabled) {
      setError("This offer is not currently open for payment.");
      return;
    }

    const payload = {
      message: buyerMessage.trim(),
    };

    if (action === "counter") {
      const amount = moneyNumber(buyerCounterAmount, null);

      if (amount === null || amount <= 0) {
        setError("Enter a valid offer amount.");
        return;
      }

      if (Number(buyerOffersLeft) <= 0) {
        setError("You have used all 3 offer chances.");
        return;
      }

      payload.amount_gbp = amount.toFixed(2);
    }

    setActionLoading(action);
    setError("");
    setSuccess("");

    try {
      if (action === "pay") {
        await redirectToPayment();
        return;
      }

      const endpointAction =
        action === "accept" ? "accept-counter" : action;

      const response = await fetch(actionUrl(endpointAction), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.message ||
            data.detail ||
            "The response could not be saved."
        );
      }

      if (action === "accept") {
        setSuccess("Counter accepted. Opening payment...");
        await redirectToPayment(data);
        return;
      }

      const messages = {
        counter: "Your offer was sent. The artist has 24 hours to respond.",
        decline: "Counter-offer declined. The artist will be notified.",
      };

      setSuccess(data.message || messages[action] || "Response saved.");
      await refreshOffer();
    } catch (err) {
      setError(err.message || "The response could not be saved.");
    } finally {
      setActionLoading("");
    }
  };

  return (
    <PageShell>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={3}>
          <DarkPanel elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} md={8}>
                <Chip
                  icon={<LocalOfferIcon />}
                  label="Secure counter-offer"
                  sx={{
                    mb: 2,
                    bgcolor: alpha(WHITE, 0.1),
                    color: WHITE,
                    border: `1px solid ${alpha(WHITE, 0.16)}`,
                    fontWeight: 900,
                    "& .MuiChip-icon": {
                      color: GOLD,
                    },
                  }}
                />

                <Typography
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontSize: { xs: "2.2rem", md: "4rem" },
                    fontWeight: 950,
                    letterSpacing: "-0.07em",
                    lineHeight: 0.96,
                  }}
                >
                  Respond to this counter-offer
                </Typography>

                <Typography
                  sx={{
                    mt: 1.5,
                    color: alpha(WHITE, 0.72),
                    lineHeight: 1.75,
                    maxWidth: 720,
                  }}
                >
                  Accept and pay, make another offer, or decline.
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                  <Box
                    sx={{
                      p: 1.4,
                      borderRadius: 4,
                      bgcolor: alpha(WHITE, 0.08),
                      border: `1px solid ${alpha(WHITE, 0.14)}`,
                    }}
                  >
                    <Typography sx={{ color: alpha(WHITE, 0.62), fontSize: 12, fontWeight: 850 }}>
                      Artist
                    </Typography>
                    <Typography sx={{ color: WHITE, fontWeight: 950 }}>
                      {artworkArtist}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.4,
                      borderRadius: 4,
                      bgcolor: alpha(GOLD, 0.13),
                      border: `1px solid ${alpha(GOLD, 0.18)}`,
                    }}
                  >
                    <Typography sx={{ color: alpha(WHITE, 0.62), fontSize: 12, fontWeight: 850 }}>
                      Artist counter
                    </Typography>
                    <Typography sx={{ color: WHITE, fontWeight: 950 }}>
                      {artistCounterLabel}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </DarkPanel>

          {loading && (
            <Panel sx={{ p: 5, textAlign: "center" }} elevation={0}>
              <CircularProgress sx={{ color: TURQUOISE_DARK, mb: 2 }} />
              <Typography sx={{ fontWeight: 950 }}>
                Opening counter-offer...
              </Typography>
            </Panel>
          )}

          {!loading && error && !offer && (
            <Panel sx={{ p: { xs: 3, md: 4 } }} elevation={0}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>

              <SoftButton
                component={RouterLink}
                to="/"
                startIcon={<HomeRoundedIcon />}
              >
                Go to Art For My Room
              </SoftButton>
            </Panel>
          )}

          {!loading && offer && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Panel elevation={0} sx={{ height: "100%" }}>
                  {artworkImageUrl ? (
                    <Box
                      component="img"
                      src={artworkImageUrl}
                      alt={artworkTitle}
                      sx={{
                        width: "100%",
                        height: { xs: 310, md: 430 },
                        display: "block",
                        objectFit: "cover",
                        bgcolor: TURQUOISE_MIST,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: { xs: 310, md: 430 },
                        display: "grid",
                        placeItems: "center",
                        bgcolor: TURQUOISE_MIST,
                      }}
                    >
                      <PaletteIcon sx={{ fontSize: 70, color: TURQUOISE_DARK }} />
                    </Box>
                  )}

                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={1.7}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          label={artworkCategory}
                          sx={{
                            bgcolor: BURGUNDY_MIST,
                            color: BURGUNDY,
                            fontWeight: 950,
                          }}
                        />

                        <Chip
                          label={statusLabel}
                          sx={{
                            bgcolor: payableStatus ? TURQUOISE_MIST : GOLD_MIST,
                            color: payableStatus ? TURQUOISE_DARK : GOLD_DARK,
                            fontWeight: 950,
                          }}
                        />
                      </Stack>

                      <Box>
                        <Typography
                          sx={{
                            fontFamily: HEADING_FONT,
                            fontWeight: 950,
                            fontSize: { xs: "1.55rem", md: "2.1rem" },
                            letterSpacing: "-0.045em",
                            lineHeight: 1.05,
                          }}
                        >
                          {artworkTitle}
                        </Typography>

                        <Typography sx={{ color: MUTED, mt: 1 }}>
                          {artworkMedium} · {artworkSize}
                        </Typography>
                      </Box>

                      <Divider />

                      <Grid container spacing={1.3}>
                        <Grid item xs={6}>
                          <MetaBox
                            icon={<LocalOfferIcon />}
                            label="Your offer"
                            value={buyerOfferLabel}
                            tone="turquoise"
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <MetaBox
                            icon={<TrendingUpIcon />}
                            label="Artist counter"
                            value={artistCounterLabel}
                            tone="gold"
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <MetaBox
                            icon={<StraightenIcon />}
                            label="Postage"
                            value={postageLabel}
                            tone="turquoise"
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <MetaBox
                            icon={<ShieldIcon />}
                            label="Total"
                            value={totalLabel}
                            tone="burgundy"
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </CardContent>
                </Panel>
              </Grid>

              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  <Panel sx={{ p: { xs: 2.5, md: 3 } }} elevation={0}>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.2}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: HEADING_FONT,
                              fontWeight: 950,
                              fontSize: "1.65rem",
                              letterSpacing: "-0.04em",
                            }}
                          >
                            Counter from artist
                          </Typography>

                          <Typography sx={{ color: MUTED }}>
                            Status: {statusLabel}
                          </Typography>
                        </Box>

                        <Chip
                          icon={<TimerIcon />}
                          label={timeLeft || "24 hours"}
                          sx={{
                            bgcolor: expiredByClock ? BURGUNDY_MIST : GOLD_MIST,
                            color: expiredByClock ? BURGUNDY : GOLD_DARK,
                            fontWeight: 950,
                          }}
                        />
                      </Stack>

                      <Grid container spacing={1.3}>
                        <Grid item xs={12} sm={4}>
                          <MetaBox
                            icon={<LocalOfferIcon />}
                            label="Your offers"
                            value={`${buyerOffersUsed}/3`}
                            tone="turquoise"
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <MetaBox
                            icon={<ReplyIcon />}
                            label="Artist counters"
                            value={`${artistCountersUsed}/3`}
                            tone="burgundy"
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <MetaBox
                            icon={<TimerIcon />}
                            label="Deadline"
                            value={expiresAtLabel || "24 hours"}
                            tone="gold"
                          />
                        </Grid>
                      </Grid>

                      {offer.artist_note && (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 4,
                            bgcolor: "#fbfffe",
                            border: `1px solid ${LINE}`,
                          }}
                        >
                          <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 850, mb: 0.5 }}>
                            Artist note
                          </Typography>
                          <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                            {offer.artist_note}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Panel>

                  <Panel sx={{ p: { xs: 2.5, md: 3 } }} elevation={0}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: HEADING_FONT,
                            fontWeight: 950,
                            fontSize: "1.65rem",
                            letterSpacing: "-0.04em",
                          }}
                        >
                          Your action
                        </Typography>

                        <Typography sx={{ color: MUTED }}>
                          Accept and pay, make another offer, or decline.
                        </Typography>
                      </Box>

                      {error && (
                        <Alert severity="error" onClose={() => setError("")}>
                          {error}
                        </Alert>
                      )}

                      {success && (
                        <Alert severity="success" onClose={() => setSuccess("")}>
                          {success}
                        </Alert>
                      )}

                      {payableStatus && !expiredByClock && (
                        <Alert severity="success">
                          The offer is accepted. You have 24 hours to complete payment.
                        </Alert>
                      )}

                      {finalStatus && (
                        <Alert severity="warning">
                          This offer already has a final status: {statusLabel}.
                        </Alert>
                      )}

                      {expiredByClock && !finalStatus && (
                        <Alert severity="warning">
                          This link has expired. Please make a new offer from the room matcher.
                        </Alert>
                      )}

                      <MinimalInput
                        fullWidth
                        label="Your new offer"
                        value={buyerCounterAmount}
                        onChange={(event) => setBuyerCounterAmount(event.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">£</InputAdornment>
                          ),
                        }}
                        helperText={`${buyerOffersLeft} offer chance${Number(buyerOffersLeft) === 1 ? "" : "s"} left`}
                      />

                      <MinimalInput
                        fullWidth
                        multiline
                        minRows={3}
                        label="Message, optional"
                        value={buyerMessage}
                        onChange={(event) => setBuyerMessage(event.target.value)}
                      />

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <PrimaryButton
                          fullWidth
                          startIcon={
                            actionLoading === "accept" ? (
                              <CircularProgress size={17} color="inherit" />
                            ) : (
                              <PaymentIcon />
                            )
                          }
                          disabled={Boolean(actionLoading) || finalStatus || expiredByClock}
                          onClick={() => submitBuyerAction(payableStatus ? "pay" : "accept")}
                        >
                          {payableStatus ? "Pay now" : "Accept & pay"}
                        </PrimaryButton>

                        <BurgundyButton
                          fullWidth
                          startIcon={
                            actionLoading === "counter" ? (
                              <CircularProgress size={17} color="inherit" />
                            ) : (
                              <ReplyIcon />
                            )
                          }
                          disabled={responseActionsDisabled || Number(buyerOffersLeft) <= 0}
                          onClick={() => submitBuyerAction("counter")}
                        >
                          Counter
                        </BurgundyButton>

                        <SoftButton
                          fullWidth
                          startIcon={
                            actionLoading === "decline" ? (
                              <CircularProgress size={17} color="inherit" />
                            ) : (
                              <CloseIcon />
                            )
                          }
                          disabled={responseActionsDisabled}
                          onClick={() => submitBuyerAction("decline")}
                        >
                          Decline
                        </SoftButton>
                      </Stack>

                      <Typography sx={{ color: MUTED, fontSize: 12, lineHeight: 1.6 }}>
                        Negotiation is limited to 3 buyer offers and 3 artist counters.
                        Payment links expire after 24 hours.
                      </Typography>
                    </Stack>
                  </Panel>

                  <Panel sx={{ p: 2.5 }} elevation={0}>
                    <Stack spacing={1}>
                      <Typography sx={{ color: MUTED }}>
                        Offer floor:{" "}
                        <Box component="span" sx={{ color: INK, fontWeight: 950 }}>
                          {offerFloor}
                        </Box>
                      </Typography>

                      {acceptedAmountLabel && acceptedAmountLabel !== "TBC" && (
                        <Typography sx={{ color: MUTED }}>
                          Accepted amount:{" "}
                          <Box component="span" sx={{ color: INK, fontWeight: 950 }}>
                            {acceptedAmountLabel}
                          </Box>
                        </Typography>
                      )}
                    </Stack>
                  </Panel>
                </Stack>
              </Grid>
            </Grid>
          )}
        </Stack>
      </Container>
    </PageShell>
  );
}
